import React, { useEffect, useState } from "react";
import { AlertCircle, CheckCircle2, Info, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { formatCurrency } from "@/lib/format";
import pixService from "@/services/pixService";
import paymentService from "@/services/paymentService";
import { getPixTitle } from "./PixPaymentCard";

const FieldError = ({ message }) =>
  message ? (
    <p className="flex items-center gap-1 text-xs text-destructive">
      <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
      {message}
    </p>
  ) : null;

/**
 * Confirmação de recebimento ("Marcar Pago").
 * - Com pedido vinculado: informa valor e forma recebidos
 *   (PUT /pix/{txid}/received), o que também marca o pedido como pago.
 * - Sem pedido: apenas confirma o PIX (POST /pix/confirm/{txid}) → onConfirm(null).
 */
export default function PixReceivedModal({ open, onOpenChange, pix, onConfirm, loading }) {
  const [valueReceived, setValueReceived] = useState("");
  const [payMethod, setPayMethod] = useState("PIX");
  const [payMethods, setPayMethods] = useState([]);
  const [errors, setErrors] = useState({});

  const hasOrder = Boolean(pix?.orderCode);
  const amount = pixService.getAmount(pix);

  useEffect(() => {
    if (!open || !pix) return;
    setValueReceived(amount ? amount.toFixed(2).replace(".", ",") : "");
    setPayMethod(pix.pay_method || "PIX");
    setErrors({});
    if (pix.orderCode) {
      paymentService
        .getNames()
        .then((names) => setPayMethods(Array.isArray(names) ? names : []))
        .catch(() => setPayMethods([]));
    }
  }, [open, pix, amount]);

  if (!pix) return null;

  // Forma atual do PIX e "PIX" sempre disponíveis
  const payMethodOptions = Array.from(
    new Set(["PIX", pix.pay_method, ...payMethods].filter(Boolean))
  );

  const handleConfirm = () => {
    if (!hasOrder) {
      onConfirm(null);
      return;
    }
    const text = String(valueReceived).trim();
    const value = Number(text.includes(",") ? text.replace(/\./g, "").replace(",", ".") : text);
    const newErrors = {};
    if (!Number.isFinite(value) || value <= 0) newErrors.valueReceived = "Informe o valor recebido";
    if (!payMethod) newErrors.payMethod = "Selecione a forma de pagamento";
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    onConfirm({ value_received: Math.round(value * 100) / 100, pay_method: payMethod });
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !loading && onOpenChange(next)}>
      <DialogContent className="max-h-[90vh] max-w-md gap-4 overflow-y-auto rounded-2xl p-5">
        <DialogHeader className="flex-row items-center gap-2 space-y-0 pr-8 text-left">
          <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-brand-pistachio/30 text-primary">
            <CheckCircle2 className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <DialogTitle className="font-display text-lg text-primary">
              Confirmar recebimento
            </DialogTitle>
            <DialogDescription className="text-xs">
              Confirme que o valor entrou na conta
            </DialogDescription>
          </div>
        </DialogHeader>

        <div className="flex items-center justify-between gap-3 rounded-xl bg-surface p-3.5">
          <div className="min-w-0">
            <span className="block font-display text-base font-bold text-primary">
              {getPixTitle(pix)}
            </span>
            <span className="block truncate font-mono text-[11px] text-muted-foreground">
              {pix.txid}
            </span>
          </div>
          <span className="flex-shrink-0 font-display text-2xl font-bold tracking-tight text-primary">
            {formatCurrency(amount)}
          </span>
        </div>

        {hasOrder ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label htmlFor="pix-value-received" className="text-xs font-semibold">
                  Valor recebido (R$) <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="pix-value-received"
                  inputMode="decimal"
                  autoComplete="off"
                  value={valueReceived}
                  onChange={(e) => {
                    setValueReceived(e.target.value);
                    setErrors((prev) => ({ ...prev, valueReceived: "" }));
                  }}
                  className={cn("h-10 rounded-xl bg-surface", errors.valueReceived && "border-destructive")}
                  disabled={loading}
                />
                <FieldError message={errors.valueReceived} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pix-received-method" className="text-xs font-semibold">
                  Forma <span className="text-destructive">*</span>
                </Label>
                <Select value={payMethod} onValueChange={setPayMethod} disabled={loading}>
                  <SelectTrigger
                    id="pix-received-method"
                    className={cn("h-10 rounded-xl bg-surface", errors.payMethod && "border-destructive")}
                  >
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {payMethodOptions.map((name) => (
                      <SelectItem key={name} value={name}>
                        {name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FieldError message={errors.payMethod} />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              O pedido <strong className="text-foreground">#{pix.orderCode}</strong> também será
              marcado como pago.
            </p>
          </div>
        ) : (
          <div className="flex gap-2 rounded-xl bg-secondary p-3 text-xs text-secondary-foreground">
            <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>
              Esta cobrança não está vinculada a um pedido: ela será apenas marcada como paga.
            </span>
          </div>
        )}

        <p className="text-[11px] text-muted-foreground">
          Depois de confirmado, o recebimento não pode ser desfeito.
        </p>

        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={loading}
            className="h-10 rounded-full bg-surface-container px-4 text-[13px] font-semibold text-foreground transition-colors hover:bg-surface-high disabled:opacity-60"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={loading}
            className="flex h-10 items-center justify-center gap-1.5 rounded-full bg-primary px-4 text-[13px] font-semibold text-primary-foreground shadow-sm transition-all hover:bg-brand-dark active:scale-95 disabled:opacity-70"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {loading ? "Confirmando..." : "Confirmar"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
